import axios from "@/plugins/axios"
import type { Pagination } from "@/types/common.type"
import type { QualityDetail, QualityProduct, QualityProperty } from "@/types/quality.type"
import { normalizeQualityName } from "@/utils/qualityName"

export interface ListRequest {
  page: string | null
  perPage: string | null
  name: string | null
  status: string | null
}

export interface SwatchStockListRequest {
  page: string | null
  perPage: string | null
  name: string | null
  swatchStockStatus: string | null
  isSwatchOffered: string | null
}

// Forms serialize empty numeric inputs as null; the API validates required fields.
export interface CreateRequest {
  name: string
  hsCode: string
  countryOfOriginId: number
  isPrintOrderable: boolean
  minimumWidth: number | null
  fullWidth: number | null
  weight: number | null
  yardWeight: number | null
  categoryId: number
  patternId: number
  dyeMethodId: number
  contents: { id: number; ratio: number }[]
  yarnEffectIds: number[]
  brandYarnIds: number[]
  finishIds: number[]
  appearanceIds: number[]
  performanceIds: number[]
  sustainabilityFactorIds: number[]
}

export interface UpdateRequest {
  name: string
  hsCode: string
  countryOfOriginId: number
  isPrintOrderable: boolean
  isSwatchOffered?: boolean
  minimumWidth: number | null
  fullWidth: number | null
  weight: number | null
  yardWeight: number | null
  categoryId: number
  patternId: number
  dyeMethodId: number
  contents: { id: number; ratio: number }[]
  yarnEffectIds: number[]
  brandYarnIds: number[]
  finishIds: number[]
  appearanceIds: number[]
  performanceIds: number[]
  sustainabilityFactorIds: number[]
}

export interface UpadateProductRequest {
  products: {
    id: number
    storeColorCode: string
    priceSurplus: number
    status: string
  }[]
}

export interface UpdateSwatchOffered {
  isSwatchOffered: boolean
  reason: string | null
}

export interface UpdateQualityPriceTagRequest {
  range: string
  quantityGteq: number | null
  quantityLteq: number | null
  price: number | null
}

export interface UpdateQualityPriceTagGroupRequest {
  salesType: string
  printMethod: string
  qualityPriceTags: UpdateQualityPriceTagRequest[]
}

export interface UpdateStandardOrderPriceTagGroupRequest {
  standardOrderPriceTagGroup: UpdateQualityPriceTagGroupRequest
}

export interface UpdatePrintPriceTagGroupRequest {
  printPriceTagGroup: UpdateQualityPriceTagGroupRequest
}

async function get(id: number): Promise<QualityDetail> {
  const response = await axios.get<QualityDetail>(`/qualities/${id}`)
  return response.data
}

async function list(request: ListRequest | SwatchStockListRequest): Promise<Pagination<QualityDetail>> {
  const response = await axios.get<Pagination<QualityDetail>>("/qualities", {
    params: request,
  })
  return response.data
}

async function products(id: number): Promise<QualityProduct> {
  const response = await axios.get<QualityProduct>(`/qualities/${id}/products`)
  return response.data
}

async function properties(): Promise<QualityProperty> {
  const response = await axios.get<QualityProperty>("/qualities/properties")
  return response.data
}

async function create(request: CreateRequest): Promise<QualityDetail> {
  const response = await axios.post<QualityDetail>("/qualities", { ...request, name: normalizeQualityName(request.name) })
  return response.data
}

async function update(id: number, request: UpdateRequest): Promise<void> {
  await axios.put(`/qualities/${id}`, { ...request, name: normalizeQualityName(request.name) })
}

async function updateProducts(id: number, request: UpadateProductRequest): Promise<void> {
  await axios.put(`/qualities/${id}/products`, request)
}

async function updateSwatchOffered(id: number, request: UpdateSwatchOffered): Promise<void> {
  await axios.put(`/qualities/${id}/swatch_offered`, request)
}

async function updateStandardOrderPriceTags(
  id: number,
  request: UpdateStandardOrderPriceTagGroupRequest,
): Promise<void> {
  await axios.put(`/qualities/${id}/standard_order_price_tags`, request)
}

async function updatePrintPriceTags(id: number, request: UpdatePrintPriceTagGroupRequest): Promise<void> {
  await axios.put(`/qualities/${id}/print_price_tags`, request)
}

const qualityService = {
  get,
  list,
  products,
  properties,
  create,
  update,
  updateProducts,
  updateSwatchOffered,
  updateStandardOrderPriceTags,
  updatePrintPriceTags,
}

export default qualityService
